import React, { Component } from 'react'
import { connect } from 'react-redux'
import deepEqual from 'fast-deep-equal/react'
import pickBy from 'lodash/pickBy'
import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'
import uniq from 'lodash/uniq'
import each from 'lodash/each'
import autoBind from 'auto-bind'
import jwt from 'jsonwebtoken'
import cookies from 'js-cookie'
import pick from 'lodash/pick'
import omit from 'lodash/omit'
import debounce from 'lodash/debounce'

import MfaContainer from 'containers/MfaContainer'
import PopupContainer from 'containers/PopupContainer'

import { bindStateToProps, bindActionCreators } from 'lib'
import ReactGA4 from 'lib/ga4'
import isEqualProps from 'lib/isEqualProps'
import {
  isMobile,
  isObject,
  isEmptyObject,
  isArray,
  isFunction,
  isEmptyString
} from 'lib/detectType'
import { findErrorSolution } from 'lib/errorResponseSolution'

import { actions as coologActions } from 'store/modules/coolog'
import { actions as channelTalkActions } from 'store/modules/channelTalk'
import { actions as snackbarActions } from 'store/modules/snackbar'
import { actions as confirmActions } from 'store/modules/confirm'
import { actions as mfaActions } from 'store/modules/mfa'
import { actions as popupActions } from 'store/modules/popup'
import { actions as usersActions } from 'store/modules/users'

import CircularRequestProgress from 'components/atoms/CircularRequestProgress'

import config from 'config'

class ResponseContainer extends Component {
  constructor(props) {
    super(props)
    autoBind(this)
    this.solutionResponse = []
    this.loadingCount = []
    this.loadedCount = []
    this.loginResponseQueue = []
  }

  componentDidMount = () => {
    this.ahid = jwt.decode(cookies.get('AHID'))
  }

  shouldComponentUpdate = (nextProps, nextState) => {
    const updateFlag = !isEqualProps(
      this.getResponse(nextProps),
      this.getResponse(this.props),
      undefined,
      // response container의 경우 loading이 true여도 업데이트 합니다.
      { exceptLoading: false }
    )
    return updateFlag
  }

  getResponse(props) {
    const response = {}
    each(props, (data, key) => {
      if (/.*_Response_.*$/.test(key) && isObject(data)) {
        Object.assign(response, { [key]: cloneDeep(data) })
      }
    })
    return response
  }

  getShowLoading(response) {
    const showLoading = cloneDeep(
      pickBy(response, (responseData = {}) => {
        const { responseOptions = {}, loading } = responseData
        const { loading: loadingOptions = {} } = responseOptions
        const { ignore, display } = loadingOptions
        if (ignore === true || display === false) return false
        return loading === true
      })
    )
    if (Object.keys(showLoading).length > 0) {
      this.loadedCount.push(...Object.keys(showLoading))
    }
    return showLoading
  }

  getNewResponse(prevResponse, response) {
    return pickBy(response, (data, responseKey) => {
      if (
        !prevResponse[responseKey] ||
        !deepEqual(prevResponse[responseKey], data)
      ) {
        return true
      }
      return false
    })
  }

  checkVisibleMessage = responseData => {
    const {
      status,
      containerOptions = {},
      responseOptions = {},
      message,
      error
    } = responseData
    const { message: messageOptions = {}, error: errorOptions = {} } =
      responseOptions
    const options = error ? errorOptions : messageOptions
    if (isEmptyString(message)) {
      return false
    }
    // ignore => array or boolean
    const ignore = containerOptions?.ignore ?? options?.ignore
    if (error && ignore === true) {
      return false
    }
    if (
      error &&
      isArray(ignore) &&
      ignore.map(data => String(data)).indexOf(String(status)) !== -1
    ) {
      return false
    }
    return true
  }

  getActionNameByResponseKey = responseKey => {
    const [, actionName, moduleName] =
      responseKey.match(/(.*)_Response_(.*)/) || []
    return { actionName, moduleName }
  }

  getTokenAccountId = () => {
    const token = jwt.decode(cookies.get('CSAK')) || {}
    return token?.accountId
  }

  getGoogleAnalyticsData = (responseKey, responseData) => {
    const { auth } = this.props
    const isRequestError = responseData.error === true
    const containerOptions = responseData?.containerOptions
    const options = containerOptions?.ga ?? {}
    const { disabled = false, value } = options
    const { actionName, moduleName } =
      this.getActionNameByResponseKey(responseKey)
    const category = options?.category ?? moduleName
    const action = isRequestError
      ? `FAILED_${options?.action ?? actionName}`
      : `SUCCEED_${options?.action ?? actionName}`
    const label = options?.label ?? auth?.accountId ?? this.getTokenAccountId()
    return { actionName, moduleName, disabled, category, action, label, value }
  }

  // 성공 또는 실패의 경우 구글어날리틱스에 등록, 2차 인증 제외
  saveGoogleAnalytics = (responseKey, responseData = {}) => {
    const isMfa = responseData.mfa
    if (isMfa) return
    const isVisibleMessage = this.checkVisibleMessage(responseData)
    const { auth, token } = this.props
    const isRequestError = responseData.error === true
    const { actionName, disabled } = this.getGoogleAnalyticsData(
      responseKey,
      responseData
    )
    if (disabled === true) return
    const visible = isVisibleMessage === false ? 'INVISIBLE' : 'VISIBLE'
    const method = String(responseData?.method).toUpperCase()
    if (isRequestError) {
      const isAuthorized =
        isObject(auth) || isObject(token) ? 'Authorized' : 'Unauthorized'
      if (isVisibleMessage) {
        ReactGA4.event('failed_api_request', {
          errorRequestUrl: `[${method}] ${responseData?.url}`,
          key: responseData?.key,
          method: responseData?.method,
          errorStatus: responseData?.status,
          errorCode: responseData?.code,
          errorMessage: responseData?.message,
          errorLocation: responseData?.location,
          moduleName: responseData?.moduleName,
          actionName,
          isAuthorized,
          visible
        })
      }
    } else {
      ReactGA4.event('succeed_api_request', {
        requestUrl: `[${method}] ${responseData?.url}`,
        key: responseData?.key,
        method: responseData?.method,
        status: responseData?.status,
        location: responseData?.location,
        moduleName: responseData?.moduleName,
        actionName
      })
    }
  }

  // 서버에 로그 요청 생성
  saveRequestToCoolog = responseData => {
    try {
      const { ConfirmActions, PopupActions, ChannelTalkActions, auth, token } =
        this.props
      const currentToken = jwt.decode(cookies.get('CSAK')) || {}
      const isMfa = isObject(responseData?.mfa)
      const isError = Boolean(responseData?.error)
      // 사용자에게 보여지는 메시지만
      const isVisibleMessage = this.checkVisibleMessage(responseData)
      const needSaveLog =
        (responseData?.saveLogFlag === true && !isMfa) ||
        // 오류의 경우 사용자에게 표시되는 메시지만 기록
        (isError &&
          // 로그 표시 여부 끄지 않은 경우
          responseData?.saveLogFlag !== false &&
          isVisibleMessage === true &&
          // 2차 인증 제외
          !isMfa &&
          // 로그인 한 사용자만
          isObject(auth) &&
          // 계정전환 사용안함
          isEmptyString(currentToken?.targetAccountId))
      if (!needSaveLog) return
      const { CoologActions } = this.props
      const memberInfo = auth?.memberInfo || {}
      const errorSolution = findErrorSolution(responseData, {
        credential: { auth, token },
        actions: {
          ConfirmActions,
          PopupActions,
          ChannelTalkActions
        }
      })
      const hasErrorSolution =
        isObject(errorSolution) && !isEmptyObject(errorSolution)
      const saveData = {
        buildHash: process.env.REACT_APP_BUILD_HASH,
        ahid: this.ahid,
        auth: {
          accountId: auth?.accountId,
          memberId: auth?.memberId,
          memberInfo: omit(memberInfo, ['devices'])
        },
        isMobile: isMobile(),
        storeName: responseData?.moduleName,
        requestUrl: responseData?.url,
        requestHeaders: responseData?.headers,
        executeBy: responseData?.containerOptions?.executeBy,
        hasErrorSolution,
        ...pick(responseData, [
          'actionId',
          'actionName',
          'code',
          'method',
          'status',
          'location',
          'payload',
          'message',
          'errorMessage',
          'scriptErrorMessage',
          'mfa',
          'timing',
          'permissionDenied',
          'packageVersion'
        ])
      }
      const category = isError
        ? 'UI_SERVER_REQUEST_ERROR_LOG'
        : 'UI_SERVER_REQUEST_LOG'
      CoologActions.saveLog({
        category,
        message: responseData?.message || '(메시지 없음)',
        systemData: saveData
      })
    } catch (error) {}
  }

  // 2차인증 진행
  showMfaProcess = (responseKey, responseData) => {
    const isMfa = responseData.mfa
    if (!isMfa) {
      return
    }
    const { ConfirmActions, PopupActions, MfaActions } = this.props
    const { actionName, moduleName } =
      this.getActionNameByResponseKey(responseKey)
    const requestConfigKey = `${actionName}_RequestConfig_${moduleName}`
    const actionsKey = `${actionName}_Actions_${moduleName}`
    const { mfa, options = {} } = responseData
    const { mfa: mfaOptions = {} } = options
    const requestConfig = this.props[requestConfigKey]
    const actions = this.props[actionsKey]
    MfaActions.show({
      key: responseKey,
      mfa,
      responseData,
      requestConfig,
      actions
    })
    const { requestName = '' } = responseData?.actionOptions || {}
    PopupActions.new({
      key: responseKey,
      title:
        mfa?.options?.popupTitle ||
        (requestName
          ? `[${requestName}] 본인 인증이 필요합니다.`
          : '본인 인증이 필요합니다.'),
      padding: true,
      openFlag: true,
      actions: [
        {
          title: '문의',
          onClick: () => {
            const { ChannelTalkActions, mfa = {} } = this.props
            ChannelTalkActions.open({
              message: [
                `2차인증 문의가 있습니다.`,
                `URI:[${responseData?.method}]${responseData?.url}`,
                `AUTH_TYPE:${mfa[responseKey]?.authType}`,
                `--------------`,
                ``
              ].join('\n')
            })
          }
        },
        {
          title: '닫기',
          color: 'secondary',
          onClick: () => {
            const { mfa } = this.props
            const isMfaIng =
              Object.keys(
                pickBy(mfa, data => {
                  if (data.done === false) return true
                  return false
                })
              ).length > 0
            if (isMfaIng) {
              ConfirmActions.show({
                confirmFlag: false,
                key: 'public',
                title: '2차 인증 취소',
                message: '진행중인 2차 인증을 그만둘까요?',
                onConfirm: () => {
                  if (isFunction(responseData?.mfa?.options?.cancel)) {
                    responseData.mfa.options.cancel()
                  }
                  PopupActions.setPopupData({
                    key: responseKey,
                    openFlag: false
                  })
                }
              })
            } else {
              PopupActions.setPopupData({
                key: responseKey,
                openFlag: false
              })
            }
          }
        }
      ],
      dialogProps: {
        onEnter: () => {
          ConfirmActions.clear({ key: 'public' })
        },
        onExited: () => {
          MfaActions.clear({ mfaKey: responseKey })
        },
        disableBackdropClick: true,
        disableEscapeKeyDown: true
      },
      ...(mfaOptions.dialogProps || {})
    })
  }

  // 2차인증 정리
  cleanMfaProcess = (responseKey, responseData) => {
    const { PopupActions, MfaActions, mfa } = this.props
    const isMfa = responseData.mfa
    const hasMfaData = isObject(mfa?.[responseKey])
    const isRequestError = responseData.error === true
    if (isMfa || isRequestError || !hasMfaData) {
      return
    }
    const { options: mfaOptions = {} } = mfa[responseKey]
    const { autoPopupClose = true, successMessage } = mfaOptions
    MfaActions.done({ mfaKey: responseKey, message: successMessage })
    if (autoPopupClose === true) {
      PopupActions.setPopupData({ key: responseKey, openFlag: false })
    }
  }

  showResponseMessage = (responseKey, responseData) => {
    const {
      ConfirmActions,
      PopupActions,
      ChannelTalkActions,
      SnackbarActions,
      auth,
      token
    } = this.props
    const {
      responseOptions = {},
      message,
      error,
      isNetworkError
    } = responseData
    const isVisibleMessage = this.checkVisibleMessage(responseData)
    if (isEmptyString(message) || isVisibleMessage === false) {
      return
    }
    const isMfa = responseData.mfa
    const isRequestError = responseData.error === true
    const isAuthorized = isObject(auth) || isObject(token)
    const variant =
      isNetworkError || isMfa ? 'default' : isRequestError ? 'error' : 'success'
    const snackbarButtons = []
    const { message: messageOptions = {}, error: errorOptions = {} } =
      responseOptions
    const { helper = {} } = error ? errorOptions : messageOptions
    const errorSolution = findErrorSolution(responseData, {
      credential: { auth, token },
      actions: {
        ConfirmActions,
        PopupActions,
        ChannelTalkActions
      }
    })
    const hasErrorSolution =
      isObject(errorSolution) && !isEmptyObject(errorSolution)
    if (hasErrorSolution) {
      const { hideMessage = false, callback: solutionCallback } = errorSolution
      if (hideMessage === true) {
        return
      }
      this.pushSolutionResponse({
        errorSolution,
        message,
        solutionCallback,
        responseData
      })
    } else if (
      responseData?.error === true &&
      !isEmptyString(helper?.message) &&
      helper?.cond?.staus === responseData?.status &&
      helper?.cond?.errorCode === responseData?.code
    ) {
      snackbarButtons.push({
        name: '도움말',
        onClick: () => {
          ConfirmActions.show({
            popupMessage: true,
            title: helper?.cond?.errorCode,
            message: helper?.message
          })
        }
      })
    }
    if (!isMfa && isRequestError) {
      if (!isAuthorized) {
        snackbarButtons.push({
          name: '로그인',
          onClick: () => {
            const isLoginPage = /oauth2\/login/.test(window.location.href)
            const currentUrl = window.location.href
            const redirectUrl =
              config?.loginPage +
              (isLoginPage ? '' : `?returnUrl=${encodeURI(currentUrl)}`)
            window.location.href = redirectUrl
          }
        })
      }
      snackbarButtons.push({
        name: '채팅문의',
        onClick: () => {
          const { actionName, category } = this.getGoogleAnalyticsData(
            responseKey,
            responseData
          )
          const method = responseData?.method
          const status = responseData?.status
          const code = responseData?.code
          const isVisible = isVisibleMessage === false ? 'INVISIBLE' : 'VISIBLE'
          const formatUserAction = {
            method,
            status,
            code,
            category,
            actionName,
            isAuthorized,
            isVisible
          }
          this.onSupport(
            [
              `MESSAGE:${responseData?.message}`,
              ``,
              `ACTION_ID:${responseData?.actionId}`,
              ``,
              `ACTION:${JSON.stringify(formatUserAction)}`,
              ``,
              `RESPONSE:${JSON.stringify(
                pick(responseData, ['location', 'url', 'payload'])
              )}`
            ].join('\n')
          )
        }
      })
    }
    if (!hasErrorSolution) {
      SnackbarActions.enqueue({
        hash: responseKey,
        severity: variant,
        message,
        buttons: snackbarButtons
      })
    }
  }

  componentDidUpdate(prevProps) {
    const prevResponse = this.getResponse(prevProps)
    const response = this.getResponse(this.props)
    const newResponse = this.getNewResponse(prevResponse, response)
    each(newResponse, async (responseData = {}, responseKey) => {
      if (responseData?.loading === true) return
      // google analytics data
      this.saveGoogleAnalytics(responseKey, responseData)
      // 사용자 리퀘스트 로그
      this.saveRequestToCoolog(responseData)
      // 요청 종료 후 2차인증 정리
      this.cleanMfaProcess(responseKey, responseData)
      // 2차인증 진행
      this.showMfaProcess(responseKey, responseData)
      // 사용자 메시지 표기
      this.showResponseMessage(responseKey, responseData)
    })
  }

  onSupport = async message => {
    const { ChannelTalkActions, UsersActions } = this.props
    const {
      data: { userChatId }
    } = await UsersActions.openChats()
    const {
      data: {
        message: { chatId }
      }
    } = await UsersActions.postMessage({
      userChatId,
      message
    })
    ChannelTalkActions.open({ chatId })
  }

  pushSolutionResponse = data => {
    this.solutionResponse.push(data)
    this.setSolutionResponse(this.solutionResponse)
  }

  setSolutionResponse = debounce(solutionResponse => {
    const { CoologActions } = this.props
    CoologActions.setState({ solutionResponse: [].concat(solutionResponse) })
  }, 500)

  render() {
    const response = this.getResponse(this.props)
    const showLoading = this.getShowLoading(response)
    const totalLoadedCount = uniq(this.loadedCount).length
    const loadingCount = uniq(Object.keys(showLoading)).length
    const requestLoadingCountValue =
      ((totalLoadedCount - loadingCount) / totalLoadedCount) * 100
    const visibleCircularProgress =
      !isNaN(requestLoadingCountValue) &&
      loadingCount > 0 &&
      requestLoadingCountValue < 100
    return (
      <>
        <CircularRequestProgress
          visibleProgress={visibleCircularProgress}
          requestLoadingCountValue={requestLoadingCountValue}
        />
        {Object.keys(response)
          .filter(responseKey => {
            const res = response[responseKey]
            return (
              (res.status === 401 &&
                res.code === 'Unauthorized' &&
                isObject(res.mfa)) ||
              // 로딩중일 경우 status를 알 수 없으므로 mfaRequest 여부를 통해 2차 인증 여부 확인
              res.mfaRequest === true
            )
          })
          .map(responseKey => {
            return (
              <PopupContainer popupKey={responseKey}>
                <MfaContainer mfaKey={responseKey} />
              </PopupContainer>
            )
          })}
      </>
    )
  }
}
export default connect(
  state => {
    const modules = {}
    // 각 모듈들의 response data만 추출합니다.
    Object.keys(state).forEach(moduleName => {
      const responseData =
        isObject(state[moduleName]) &&
        isFunction(state[moduleName].get) &&
        state[moduleName].get('responseData') &&
        state[moduleName].get('requestConfig')
      if (responseData) {
        // responseKey 존재하지 않으면 actionName
        Object.keys(responseData).forEach(responseKey => {
          if (responseKey === 'undefined') return
          const propName = responseKey + '_Response_' + moduleName
          const reqConfigPropName = responseKey + '_RequestConfig_' + moduleName
          const actionsPropName = responseKey + '_Actions_' + moduleName
          Object.assign(
            modules,
            merge(modules, {
              [moduleName]: [
                {
                  [propName]: `responseData.${responseKey}`,
                  [reqConfigPropName]: `requestConfig.${responseKey}`,
                  [actionsPropName]: `actions.${responseKey}`
                }
              ]
            })
          )
        })
      }
    })
    modules.mfa = isArray(modules.mfa) ? ['mfa', ...modules.mfa] : ['mfa']
    modules.users = isArray(modules.users) ? [...modules.users] : []
    modules.consoleLayout = ['auth', 'token', 'routeParams']
    return bindStateToProps(state, modules)
  },
  bindActionCreators({
    usersActions,
    snackbarActions,
    confirmActions,
    popupActions,
    mfaActions,
    channelTalkActions,
    coologActions
  })
)(ResponseContainer)
